import { useState } from 'react';
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/use-subscription";
import { Crown, CreditCard, Calendar, TrendingUp, CheckCircle, ArrowRight, Zap, Users, Shield, Star, AlertTriangle, Clock, Gift, Tag, X, Loader2 } from "lucide-react";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useMutation, useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { PLANS, type BillingCycle } from '@shared/plan-config';

interface PublicCoupon {
  id: number;
  code: string;
  description: string | null;
  discount_type: 'percentage' | 'flat' | 'lifetime';
  discount_value: string;
  applicable_plans: string[] | null;
  expiry_date: string | null;
}

interface ValidatedCoupon {
  valid: boolean;
  code: string;
  description: string | null;
  discount_type: 'percentage' | 'flat' | 'lifetime';
  discount_value: number;
}

// Plan configuration with upgrade/downgrade paths
const PLAN_CONFIG = {
  trial: {
    name: 'Trial',
    price: 0,
    icon: Gift,
    color: 'orange',
    features: ['Up to 2 users (including admin)', 'Up to 25 people management', 'Up to 50 applications', '120 license limit', 'Basic reporting', '14-day trial'],
    upgradeOptions: ['single', 'team', 'enterprise'],
    downgradeOptions: []
  },
  single: {
    name: PLANS.single.name,
    price: PLANS.single.monthly.price,
    icon: Zap,
    color: 'blue',
    features: PLANS.single.features,
    upgradeOptions: ['team', 'enterprise'],
    downgradeOptions: []
  },
  team: {
    name: PLANS.team.name,
    price: PLANS.team.monthly.price,
    icon: Users,
    color: 'purple',
    features: PLANS.team.features,
    upgradeOptions: ['enterprise'],
    downgradeOptions: ['single']
  },
  enterprise: {
    name: PLANS.enterprise.name,
    price: PLANS.enterprise.monthly.price,
    icon: Shield,
    color: 'gold',
    features: PLANS.enterprise.features,
    upgradeOptions: [],
    downgradeOptions: ['team', 'single']
  }
};

export default function ModernSubscriptionPage() {
  const { user } = useAuth();
  const { subscription, planType, isLoading, error, usage, limits } = useSubscription();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [upgradeBillingCycle, setUpgradeBillingCycle] = useState<BillingCycle>('monthly');
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<ValidatedCoupon | null>(null);
  const [couponError, setCouponError] = useState('');
  const { toast } = useToast();

  const { data: publicCoupons = [] } = useQuery<PublicCoupon[]>({
    queryKey: ['/api/public-coupons'],
  });

  const validateCouponMutation = useMutation({
    mutationFn: async (code: string) => {
      const res = await fetch('/api/coupons/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, plan: upgradeDialogPlan || planType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid coupon');
      return data as ValidatedCoupon;
    },
    onSuccess: (data) => {
      setAppliedCoupon(data);
      setCouponError('');
      toast({
        title: 'Coupon applied!',
        description: data.description || (
          data.discount_type === 'percentage' ? `${data.discount_value}% off`
            : data.discount_type === 'lifetime' ? `$${data.discount_value} one-time — lifetime access`
            : `$${data.discount_value} off`
        ),
      });
    },
    onError: (err: Error) => {
      setCouponError(err.message);
      setAppliedCoupon(null);
    },
  });

  const [upgradeDialogPlan, setUpgradeDialogPlan] = useState<string | null>(null);

  const upgradeMutation = useMutation({
    mutationFn: async ({ targetPlan, billingCycle, couponCode }: { targetPlan: string; billingCycle: BillingCycle; couponCode?: string }) => {
      if (!PLANS[targetPlan]) {
        throw new Error('Invalid plan selected');
      }

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: targetPlan, billingCycle, ...(couponCode ? { couponCode } : {}) })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({
          title: "Upgrade Failed",
          description: "Could not start upgrade process. Please try again.",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Upgrade Failed",
        description: error.message || "Could not start upgrade process. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsUpgrading(false);
    }
  });

  const handleUpgrade = (targetPlan: string) => {
    setCouponInput('');
    setAppliedCoupon(null);
    setCouponError('');
    setUpgradeDialogPlan(targetPlan);
  };

  // Lifetime deals only exist for the Single plan today — surface the coupon-driven
  // one-time-payment path (already used by the public pricing page) directly in the
  // in-app upgrade flow instead of requiring the user to know a promo code.
  const handleUpgradeLifetime = (deal: PublicCoupon) => {
    setCouponInput(deal.code);
    setCouponError('');
    setUpgradeDialogPlan('single');
    setAppliedCoupon({
      valid: true,
      code: deal.code,
      description: deal.description,
      discount_type: 'lifetime',
      discount_value: parseFloat(deal.discount_value),
    });
  };

  const proceedToCheckout = (couponCode?: string) => {
    if (!upgradeDialogPlan) return;
    setIsUpgrading(true);
    setUpgradeDialogPlan(null);
    upgradeMutation.mutate({ targetPlan: upgradeDialogPlan, billingCycle: upgradeBillingCycle, couponCode });
  };

  // Opens Stripe's hosted billing portal for invoices, payment method updates, and
  // cancellation. Plan switching is intentionally not offered there — see
  // /api/subscription/portal-session for why.
  const manageBillingMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/subscription/portal-session', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to open billing portal');
      return data;
    },
    onSuccess: (data) => {
      if (data.url) window.location.href = data.url;
    },
    onError: (error: Error) => {
      toast({ title: 'Could not open billing portal', description: error.message, variant: 'destructive' });
    },
  });

  const [downgradeDialogPlan, setDowngradeDialogPlan] = useState<string | null>(null);
  const [downgradeViolations, setDowngradeViolations] = useState<{ label: string; current: number; limit: number }[] | null>(null);

  const downgradeMutation = useMutation({
    mutationFn: async (targetPlan: string) => {
      const response = await fetch('/api/subscription/downgrade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetPlan }),
      });
      const data = await response.json();
      if (!response.ok) {
        const err: any = new Error(data.error || 'Failed to schedule downgrade');
        err.violations = data.violations;
        throw err;
      }
      return data;
    },
    onSuccess: (data) => {
      setDowngradeDialogPlan(null);
      setDowngradeViolations(null);
      toast({ title: 'Downgrade scheduled', description: data.message });
    },
    onError: (error: any) => {
      if (error.violations?.length) {
        setDowngradeViolations(error.violations);
      } else {
        toast({ title: 'Downgrade failed', description: error.message, variant: 'destructive' });
        setDowngradeDialogPlan(null);
      }
    },
  });

  const handleDowngrade = (targetPlan: string) => {
    setDowngradeViolations(null);
    setDowngradeDialogPlan(targetPlan);
  };

  function getDiscountedPrice(originalPrice: number): { discounted: number; savings: number } | null {
    if (!appliedCoupon || appliedCoupon.discount_type === 'lifetime') return null;
    const savings = appliedCoupon.discount_type === 'percentage'
      ? originalPrice * (appliedCoupon.discount_value / 100)
      : Math.min(appliedCoupon.discount_value, originalPrice);
    return { discounted: Math.max(0, originalPrice - savings), savings };
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="h-12 bg-white/50 rounded-lg animate-pulse"></div>
          <div className="grid gap-6 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-96 bg-white/50 rounded-xl animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-7xl mx-auto">
          <Alert variant="destructive" className="bg-white">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load subscription information. Please try refreshing the page.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const currentPlan = PLAN_CONFIG[planType as keyof typeof PLAN_CONFIG] || PLAN_CONFIG.trial;
  // Check if trial has expired based on backend flags
  const isTrialExpired = subscription?.trialExpired === true;
  const isTrialUser = (subscription?.isTrialUser || planType === 'trial') && !isTrialExpired;
  const trialDaysLeft = subscription?.trialDaysLeft || 0;
  const upgradeOptions = currentPlan.upgradeOptions;
  // Lifetime tenants have no recurring subscription to downgrade, and are already on
  // the cheapest tier — no downgrade path applies to them. Accounts with no real Stripe
  // subscription (e.g. manually activated by an admin) can still downgrade — it just
  // applies immediately instead of at a billing period's end (see the backend route).
  const downgradeOptions = subscription?.plan?.interval === 'lifetime' ? [] : currentPlan.downgradeOptions;
  const hasRealSubscription = Boolean(subscription?.stripeSubscriptionId);

  // Only offer the lifetime deal to Single-plan users, and only when a real public
  // lifetime coupon exists for it (admin-managed via /admin/coupons).
  const lifetimeDeal = planType === 'single'
    ? publicCoupons.find(c => c.discount_type === 'lifetime' && (!c.applicable_plans?.length || c.applicable_plans.includes('single')))
    : undefined;

  const expiredTrialMessage = subscription?.message || 'Your trial has expired. Please choose a plan to continue.';

  // Billing interval of the active subscription ('month', 'year', or 'lifetime')
  const isLifetime = subscription?.plan?.interval === 'lifetime';
  const billingInterval = subscription?.plan?.interval === 'year' ? 'year' : 'month';
  const currentPlanConfig = PLANS[planType as keyof typeof PLANS];
  const displayPrice = currentPlanConfig
    ? (billingInterval === 'year' ? currentPlanConfig.yearly.price : currentPlanConfig.monthly.price)
    : currentPlan.price;

  // Calculate usage percentages
  const userUsagePercent = limits.maxUsers ? Math.round((usage.users / limits.maxUsers) * 100) : 0;
  const licenseUsagePercent = limits.maxLicenses ? Math.round((usage.licenses / limits.maxLicenses) * 100) : 0;
  const appUsagePercent = limits.maxApplications ? Math.round((usage.applications / limits.maxApplications) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      <div className="max-w-6xl mx-auto p-4 space-y-6">

        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center space-x-3">
            <SaaSGamaLogo size="md" />
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                SaaSGama Subscription
              </h1>
              <p className="text-slate-600 text-sm mt-0.5">
                Manage your plan and unlock premium features
              </p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-5">
          <TabsList className="grid w-full grid-cols-3 lg:w-80 mx-auto bg-white/90 backdrop-blur-sm border border-slate-200">
            <TabsTrigger value="overview" className="flex items-center space-x-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <TrendingUp className="h-4 w-4" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="billing" className="flex items-center space-x-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <CreditCard className="h-4 w-4" />
              <span>Billing</span>
            </TabsTrigger>
            <TabsTrigger value="upgrade" className="flex items-center space-x-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Star className="h-4 w-4" />
              <span>Upgrade</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-5">
            {/* Expired Trial Alert Banner */}
            {isTrialExpired && (
              <Alert className="bg-red-50 border-2 border-red-200 shadow-md">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <AlertDescription className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-red-800">
                      ⚠️ {expiredTrialMessage}
                    </span>
                    <p className="text-red-700 text-sm mt-1">
                      Choose one of the paid plans below to restore access.
                    </p>
                  </div>
                  <Badge variant="destructive" className="font-semibold">
                    EXPIRED
                  </Badge>
                </AlertDescription>
              </Alert>
            )}

            {/* Trial Alert Banner */}
            {isTrialUser && (
              <Alert className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 shadow-md">
                <Clock className="h-5 w-5 text-orange-600" />
                <AlertDescription className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-orange-800">
                      🎉 You're on a FREE 14-day trial!
                    </span>
                    <p className="text-orange-700 text-sm mt-1">
                      Explore all features with no commitment. {trialDaysLeft} days remaining.
                    </p>
                  </div>
                  <Badge variant="secondary" className="bg-orange-100 text-orange-800 font-semibold">
                    {trialDaysLeft} days left
                  </Badge>
                </AlertDescription>
              </Alert>
            )}

            {/* Current Plan Card */}
            <Card className={`${isTrialUser 
              ? 'bg-white/90 backdrop-blur-sm border-2 border-orange-200' 
              : 'bg-white/90 backdrop-blur-sm border border-slate-200'} shadow-lg`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2.5 rounded-lg text-white ${isTrialUser 
                      ? 'bg-gradient-to-r from-orange-500 to-amber-600' 
                      : 'bg-gradient-to-r from-blue-600 to-blue-700'}`}>
                      <currentPlan.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-xl flex items-center space-x-2">
                        <span>{currentPlan.name} Plan</span>
                        {isTrialUser ? (
                          <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-xs font-semibold">
                            🎁 FREE TRIAL
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs">
                            Current
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="text-slate-600 text-sm">
                        {isTrialExpired
                          ? `Trial ended on ${new Date(subscription?.trialEndDate || new Date()).toLocaleDateString()}`
                          : isTrialUser
                            ? `FREE for ${trialDaysLeft} more days • Then $${PLANS.single.monthly.price}/month`
                            : isLifetime
                              ? 'Lifetime access • One-time payment'
                              : `$${displayPrice}/${billingInterval} • Active subscription`}
                      </CardDescription>
                    </div>
                  </div>
                  {(subscription?.plan?.status || isTrialExpired || isTrialUser) && (
                    <Badge 
                      variant={isTrialExpired ? 'destructive' : ((subscription?.plan?.status === 'active' || subscription?.plan?.status === 'lifetime' || subscription?.plan?.status === 'trialing' || isTrialUser) ? 'default' : 'secondary')}
                      className={`capitalize ${isTrialExpired 
                        ? 'bg-red-600 text-white' 
                        : isTrialUser 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-blue-600'}`}
                    >
                      {isTrialExpired ? 'TRIAL EXPIRED' : (isTrialUser ? 'TRIAL ACTIVE' : subscription?.plan?.status)}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Usage Statistics */}
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Users</span>
                      <span className="font-medium text-blue-700">{usage.users} / {limits.maxUsers === 999999 ? '∞' : limits.maxUsers}</span>
                    </div>
                    <Progress value={userUsagePercent} className="h-1.5" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Licenses</span>
                      <span className="font-medium text-blue-700">{usage.licenses} / {limits.maxLicenses === 999999 ? '∞' : limits.maxLicenses}</span>
                    </div>
                    <Progress value={licenseUsagePercent} className="h-1.5" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Applications</span>
                      <span className="font-medium text-blue-700">{usage.applications} / {limits.maxApplications === 999999 ? '∞' : limits.maxApplications}</span>
                    </div>
                    <Progress value={appUsagePercent} className="h-1.5" />
                  </div>
                </div>

                <Separator className="my-3" />

                {/* Features */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Plan Features</h4>
                  <div className="grid gap-1.5 md:grid-cols-2">
                    {currentPlan.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="billing" className="space-y-5">
            {/* Trial Billing Notice */}
            {isTrialUser && (
              <Alert className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 shadow-md">
                <Gift className="h-5 w-5 text-green-600" />
                <AlertDescription>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-green-800">
                        🎁 FREE Trial - No payment required yet!
                      </span>
                      <p className="text-green-700 text-sm mt-1">
                        Your trial will automatically convert to the Single plan (${PLANS.single.monthly.price}/month) after {trialDaysLeft} days unless you cancel.
                      </p>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <Card className={`${isTrialUser 
              ? 'bg-white/90 backdrop-blur-sm border-2 border-green-200' 
              : 'bg-white/90 backdrop-blur-sm border border-slate-200'} shadow-lg`}>
              <CardHeader className="pb-3">
                <CardTitle className={`flex items-center space-x-2 ${isTrialUser ? 'text-green-900' : 'text-blue-900'}`}>
                  <CreditCard className={`h-5 w-5 ${isTrialUser ? 'text-green-600' : 'text-blue-600'}`} />
                  <span>{isTrialUser ? 'Trial Information' : 'Billing Information'}</span>
                </CardTitle>
                <CardDescription className="text-sm">
                  {isTrialUser ? 'Your free trial details and upcoming billing' : 'Your subscription and payment details'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <div>
                      <h4 className={`font-medium mb-1 ${isTrialUser ? 'text-green-900' : 'text-blue-900'}`}>Current Plan</h4>
                      <p className="text-base font-semibold text-slate-800">
                        {isTrialUser ? 'FREE TRIAL' : (subscription?.plan?.name || planType || 'Single')}
                      </p>
                      <p className="text-sm text-slate-600">
                        Status: <span className={`font-medium ${isTrialUser ? 'text-green-600' : 'text-blue-600'}`}>
                          {isTrialUser ? 'Trial Active' : (subscription?.plan?.status || subscription?.status || 'Active')}
                        </span>
                      </p>
                    </div>
                    
                    <div>
                      <h4 className={`font-medium mb-1 ${isTrialUser ? 'text-green-900' : 'text-blue-900'}`}>Account</h4>
                      <p className="text-sm text-slate-600">
                        {user?.fullName}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <h4 className={`font-medium mb-1 ${isTrialUser ? 'text-green-900' : 'text-blue-900'}`}>
                        {isTrialUser ? 'Trial Ends' : isLifetime ? 'Renewal' : 'Next Billing Date'}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <Calendar className={`h-4 w-4 ${isTrialUser ? 'text-green-600' : 'text-blue-600'}`} />
                        <span className="text-sm text-slate-600">
                          {isTrialUser ? (
                            subscription?.plan?.currentPeriodEnd ?
                            new Date(subscription.plan.currentPeriodEnd).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : 'In 14 days'
                          ) : isLifetime ? (
                            'Lifetime access — no further payments'
                          ) : (
                            subscription?.plan?.nextBillingDate ?
                            new Date(subscription.plan.nextBillingDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            }) : (billingInterval === 'year' ? 'Yearly billing' : 'Monthly billing')
                          )}
                        </span>
                      </div>
                    </div>

                    <div>
                      <h4 className={`font-medium mb-1 ${isTrialUser ? 'text-green-900' : 'text-blue-900'}`}>
                        {isTrialUser ? 'After Trial' : 'Subscription ID'}
                      </h4>
                      <p className="text-sm text-slate-600 font-mono">
                        {isTrialUser ?
                          `$${PLANS.single.monthly.price}/month (Single Plan)` :
                          isLifetime ?
                            'Lifetime deal' :
                          (subscription?.stripeSubscriptionId ?
                            `...${subscription.stripeSubscriptionId.slice(-8)}` :
                            'Active subscription'
                          )
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {isTrialUser ? (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-100/50 rounded-lg p-3 border border-green-200/50">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-green-900">Current Cost</span>
                      <span className="text-lg font-bold text-green-700">
                        FREE for {trialDaysLeft} more days
                      </span>
                    </div>
                  </div>
                ) : isLifetime ? (
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg p-3 border border-blue-200/50">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-blue-900">Lifetime Access</span>
                      <span className="text-lg font-bold text-blue-700">
                        One-time payment
                      </span>
                    </div>
                  </div>
                ) : (
                  subscription?.plan?.amount && (
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg p-3 border border-blue-200/50">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-blue-900">{billingInterval === 'year' ? 'Yearly Charge' : 'Monthly Charge'}</span>
                        <span className="text-lg font-bold text-blue-700">
                          ${(subscription.plan.amount / 100).toFixed(2)} {subscription.plan.currency?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  )
                )}

                {!isTrialUser && (
                  <div className="pt-2">
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() => manageBillingMutation.mutate()}
                      disabled={manageBillingMutation.isPending}
                    >
                      {manageBillingMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="mr-2 h-4 w-4" />
                      )}
                      Manage Billing
                    </Button>
                    <p className="text-xs text-slate-500 mt-1.5">
                      View and download invoices, update your payment method, or cancel your plan.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="upgrade" className="space-y-5">
            {upgradeOptions.length > 0 ? (
              <>
                <div className="text-center space-y-1">
                  <h2 className="text-xl font-bold text-blue-900">Upgrade Your Plan</h2>
                  <p className="text-slate-600 text-sm">
                    Unlock more features and capabilities for your organization
                  </p>
                </div>

                {/* Public coupon banners (lifetime deals get their own dedicated card below instead) */}
                {publicCoupons.filter(c => c.discount_type !== 'lifetime').length > 0 && (
                  <div className="space-y-2">
                    {publicCoupons.filter(c => c.discount_type !== 'lifetime').map(c => (
                      <Alert key={c.id} className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
                        <Tag className="h-4 w-4 text-green-600" />
                        <AlertDescription className="flex items-center justify-between">
                          <span className="text-green-800 text-sm">
                            <strong>{c.description || `${c.discount_type === 'percentage' ? c.discount_value + '% off' : '$' + c.discount_value + ' off'}`}</strong>
                            {' — Use code '}
                            <button
                              className="font-mono font-bold bg-green-100 px-1.5 py-0.5 rounded text-green-900 hover:bg-green-200 transition-colors"
                              onClick={() => setCouponInput(c.code)}
                            >
                              {c.code}
                            </button>
                            {c.applicable_plans?.length ? ` (${c.applicable_plans.join(', ')} plans)` : ''}
                          </span>
                          {c.expiry_date && (
                            <span className="text-xs text-green-600 ml-2 shrink-0">
                              Expires {new Date(c.expiry_date).toLocaleDateString()}
                            </span>
                          )}
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}

                {/* Billing cycle toggle */}
                <div className="flex items-center justify-center">
                  <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setUpgradeBillingCycle('monthly')}
                      className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${
                        upgradeBillingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setUpgradeBillingCycle('yearly')}
                      className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${
                        upgradeBillingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                      }`}
                    >
                      Yearly
                    </button>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {lifetimeDeal && (
                    <Card className="relative border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-white transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">
                      <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-2.5 py-0.5 text-xs">
                          Best Value
                        </Badge>
                      </div>

                      <CardHeader className="text-center pb-3">
                        <div className="mx-auto p-3 rounded-xl w-fit bg-gradient-to-r from-amber-500 to-amber-600 text-white mb-3">
                          <Crown className="h-6 w-6" />
                        </div>
                        <CardTitle className="text-lg">Lifetime</CardTitle>
                        <CardDescription className="space-y-0.5">
                          <div className="text-2xl font-bold text-slate-900">
                            ${parseFloat(lifetimeDeal.discount_value).toFixed(0)}
                            <span className="text-sm font-normal text-slate-500"> once</span>
                          </div>
                          <div className="text-xs text-slate-400 line-through">${PLANS.single.monthly.price}/month</div>
                        </CardDescription>
                      </CardHeader>

                      <CardContent className="space-y-3">
                        <div className="space-y-2">
                          {PLAN_CONFIG.single.features.map((feature, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <CheckCircle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                              <span className="text-sm text-slate-700">{feature}</span>
                            </div>
                          ))}
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                            <span className="text-sm font-medium text-slate-900">Pay once, never billed again</span>
                          </div>
                        </div>

                        <Button
                          className="w-full mt-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                          onClick={() => handleUpgradeLifetime(lifetimeDeal)}
                          disabled={isUpgrading}
                        >
                          Get Lifetime Access
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                  {upgradeOptions.map((planKey) => {
                    const plan = PLAN_CONFIG[planKey as keyof typeof PLAN_CONFIG];
                    const planPricing = PLANS[planKey as keyof typeof PLANS];
                    const price = upgradeBillingCycle === 'yearly' ? planPricing.yearly.price : planPricing.monthly.price;
                    const isPopular = planKey === 'team';
                    return (
                      <Card
                        key={planKey}
                        className={`relative transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
                          isPopular
                            ? 'border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-white'
                            : 'bg-white hover:border-blue-300 border border-slate-200'
                        }`}
                      >
                        {isPopular && (
                          <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                            <Badge className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-2.5 py-0.5 text-xs">
                              Most Popular
                            </Badge>
                          </div>
                        )}

                        <CardHeader className="text-center pb-3">
                          <div className="mx-auto p-3 rounded-xl w-fit bg-gradient-to-r from-blue-600 to-blue-700 text-white mb-3">
                            <plan.icon className="h-6 w-6" />
                          </div>
                          <CardTitle className="text-lg">{plan.name}</CardTitle>
                          <CardDescription className="space-y-0.5">
                            <div className="text-2xl font-bold text-slate-900">
                              ${price}
                              <span className="text-sm font-normal text-slate-500">/{upgradeBillingCycle === 'yearly' ? 'year' : 'month'}</span>
                            </div>
                          </CardDescription>
                        </CardHeader>

                        <CardContent className="space-y-3">
                          <div className="space-y-2">
                            {plan.features.map((feature, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <CheckCircle className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />
                                <span className="text-sm text-slate-700">{feature}</span>
                              </div>
                            ))}
                          </div>

                          <Button
                            className={`w-full mt-4 ${
                              isPopular
                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                                : 'border-blue-600 text-blue-600 hover:bg-blue-50'
                            }`}
                            onClick={() => handleUpgrade(planKey)}
                            disabled={isUpgrading}
                            variant={isPopular ? 'default' : 'outline'}
                          >
                            {isUpgrading ? (
                              <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                <span>Processing...</span>
                              </div>
                            ) : (
                              <>
                                Upgrade to {plan.name}
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            ) : (
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                <CardContent className="text-center py-8">
                  <div className="flex justify-center mb-4">
                    <SaaSGamaLogo size="lg" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-blue-900">You're on our top plan!</h3>
                  <p className="text-slate-600 max-w-md mx-auto text-sm">
                    You have access to all our premium features and capabilities. Thank you for being a valued SaaSGama customer!
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Downgrade */}
            {downgradeOptions.length > 0 && (
              <Card className="bg-white border border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Downgrade Plan</CardTitle>
                  <CardDescription className="text-sm">
                    {hasRealSubscription
                      ? "Takes effect at the end of your current billing period — you keep your current plan's features until then, with no immediate loss of access."
                      : "Takes effect immediately."}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {downgradeOptions.map((planKey) => {
                      const plan = PLAN_CONFIG[planKey as keyof typeof PLAN_CONFIG];
                      const planPricing = PLANS[planKey as keyof typeof PLANS];
                      return (
                        <div key={planKey} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                          <div>
                            <p className="font-medium text-slate-900">{plan.name}</p>
                            <p className="text-sm text-slate-500">${planPricing.monthly.price}/month</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => handleDowngrade(planKey)}>
                            Downgrade
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Usage Guidelines */}
            <Card className="bg-blue-50/50 border border-blue-200/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-blue-900">Important Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 text-sm text-slate-600">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Upgrades take effect immediately upon payment completion</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Billing cycles from the upgrade date for the next month</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>All existing data and settings are preserved during upgrades</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-3.5 w-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Downgrades take effect at the end of your current billing period, and are blocked if your current usage exceeds the target plan's limits</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Upgrade confirmation dialog with optional coupon entry */}
      <Dialog open={!!upgradeDialogPlan} onOpenChange={(open) => !open && setUpgradeDialogPlan(null)}>
        <DialogContent className="max-w-md">
          {upgradeDialogPlan && (() => {
            const dialogPlan = PLAN_CONFIG[upgradeDialogPlan as keyof typeof PLAN_CONFIG];
            const dialogPricing = PLANS[upgradeDialogPlan as keyof typeof PLANS];
            const dialogPrice = upgradeBillingCycle === 'yearly' ? dialogPricing.yearly.price : dialogPricing.monthly.price;
            const discount = getDiscountedPrice(dialogPrice);
            const interval = upgradeBillingCycle === 'yearly' ? 'year' : 'month';
            const isLifetimeCoupon = appliedCoupon?.discount_type === 'lifetime';
            return (
              <>
                <DialogHeader>
                  <DialogTitle>{isLifetimeCoupon ? 'Get Lifetime Access' : `Upgrade to ${dialogPlan.name}`}</DialogTitle>
                  <DialogDescription>
                    {isLifetimeCoupon
                      ? 'Pay once and keep Single plan access for good — no recurring billing.'
                      : 'Have a coupon code? Apply it below, or skip to continue at the regular price.'}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                  <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5">
                    {isLifetimeCoupon ? (
                      <div className="space-y-0.5">
                        <div className="text-xl font-bold text-slate-900">
                          ${appliedCoupon!.discount_value.toFixed(2)}<span className="text-sm font-normal text-slate-500"> one-time</span>
                        </div>
                        <div className="text-sm text-slate-400 line-through">${dialogPrice}/{interval}</div>
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">
                          Lifetime access — never billed again
                        </Badge>
                      </div>
                    ) : discount ? (
                      <div className="space-y-0.5">
                        <div className="text-xl font-bold text-slate-900">
                          ${discount.discounted.toFixed(2)}<span className="text-sm font-normal text-slate-500">/{interval}</span>
                        </div>
                        <div className="text-sm text-slate-400 line-through">${dialogPrice}/{interval}</div>
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">
                          Save ${discount.savings.toFixed(2)}
                        </Badge>
                      </div>
                    ) : (
                      <div className="text-xl font-bold text-slate-900">
                        ${dialogPrice}<span className="text-sm font-normal text-slate-500">/{interval}</span>
                      </div>
                    )}
                  </div>

                  {appliedCoupon ? (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-md px-3 py-2">
                      <Tag className="h-4 w-4 text-green-600 shrink-0" />
                      <span className="text-sm text-green-800 flex-1">
                        <strong>{appliedCoupon.code}</strong>
                        {' — '}
                        {appliedCoupon.description || (
                          appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}% off`
                            : appliedCoupon.discount_type === 'lifetime' ? `$${appliedCoupon.discount_value} one-time — lifetime access`
                            : `$${appliedCoupon.discount_value} off`
                        )}
                      </span>
                      <button onClick={() => { setAppliedCoupon(null); setCouponInput(''); }} className="text-green-600 hover:text-green-800">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-1.5">Have a coupon code?</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter coupon code"
                          value={couponInput}
                          onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                          onKeyDown={e => e.key === 'Enter' && couponInput.trim() && validateCouponMutation.mutate(couponInput.trim())}
                          className={couponError ? 'border-red-400' : ''}
                        />
                        <Button
                          variant="outline"
                          onClick={() => couponInput.trim() && validateCouponMutation.mutate(couponInput.trim())}
                          disabled={!couponInput.trim() || validateCouponMutation.isPending}
                        >
                          {validateCouponMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                      {couponError && <p className="text-xs text-red-500 mt-1.5">{couponError}</p>}
                    </div>
                  )}
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
                  {/* Once a coupon is applied (including the pre-applied lifetime deal), "Skip"
                      is hidden — it used to silently discard the discount and charge full
                      recurring price. Removing the coupon chip's "x" brings Skip back. */}
                  {!appliedCoupon && (
                    <Button
                      variant="ghost"
                      className="sm:mr-auto"
                      onClick={() => proceedToCheckout()}
                    >
                      Skip
                    </Button>
                  )}
                  <Button
                    onClick={() => proceedToCheckout(appliedCoupon?.code)}
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                  >
                    Continue to checkout
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Downgrade confirmation dialog, with usage/limits violations if blocked */}
      <Dialog open={!!downgradeDialogPlan} onOpenChange={(open) => !open && setDowngradeDialogPlan(null)}>
        <DialogContent className="max-w-md">
          {downgradeDialogPlan && (() => {
            const dialogPlan = PLAN_CONFIG[downgradeDialogPlan as keyof typeof PLAN_CONFIG];
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Downgrade to {dialogPlan.name}</DialogTitle>
                  <DialogDescription>
                    {hasRealSubscription
                      ? "This takes effect at the end of your current billing period — you keep your current plan's features until then."
                      : "This takes effect immediately."}
                  </DialogDescription>
                </DialogHeader>

                {downgradeViolations?.length ? (
                  <div className="space-y-2">
                    <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2.5">
                      <p className="text-sm font-medium text-red-800 mb-1.5">
                        Your current usage exceeds the {dialogPlan.name} plan's limits:
                      </p>
                      <ul className="text-sm text-red-700 space-y-0.5">
                        {downgradeViolations.map((v) => (
                          <li key={v.label}>
                            {v.label}: {v.current} (limit: {v.limit})
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-xs text-slate-500">
                      Reduce usage below these limits before downgrading, or contact support.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">
                    Are you sure you want to downgrade? You'll lose access to {currentPlan.name}-only
                    features once the change takes effect.
                  </p>
                )}

                <DialogFooter>
                  <Button variant="ghost" onClick={() => setDowngradeDialogPlan(null)}>
                    Cancel
                  </Button>
                  {!downgradeViolations?.length && (
                    <Button
                      variant="destructive"
                      onClick={() => downgradeMutation.mutate(downgradeDialogPlan)}
                      disabled={downgradeMutation.isPending}
                    >
                      {downgradeMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Confirm Downgrade
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}